// frontend-react/src/components/RuleManagement/RulePreviewTable.tsx
import React, { useEffect, useState } from 'react';
import { Table, Button, Input, Tooltip, Space, Popconfirm } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { RuleRow } from '../../types/rule';

interface RulePreviewTableProps {
  rules: RuleRow[];
  expandedRows: string[];
  onExpandChange: (expandedKeys: string[]) => void;
  showExpandAll?: boolean;
  // 編輯相關（可選，不傳則純唯讀）
  editMode?: boolean; // 是否已進入編輯模式（由 Modal 控制）
  editingRowKey?: string | null;
  savingRow?: boolean;
  onEditRow?: (key: string) => void;
  onCancelEdit?: () => void;
  onSaveRow?: (key: string, name: string, content: string) => void;
  onDeleteRow?: (key: string) => void;
}

const RulePreviewTable: React.FC<RulePreviewTableProps> = ({
  rules,
  expandedRows,
  onExpandChange,
  showExpandAll = true,
  editMode = false,
  editingRowKey = null,
  savingRow = false,
  onEditRow,
  onCancelEdit,
  onSaveRow,
  onDeleteRow,
}) => {
  const [draftName, setDraftName] = useState('');
  const [draftContent, setDraftContent] = useState('');

  useEffect(() => {
    if (editingRowKey) {
      const row = rules.find(r => r.key === editingRowKey);
      if (row) {
        setDraftName(row.規則名稱);
        setDraftContent(row.規則內容);
      }
    }
  }, [editingRowKey, rules]);

  const columns = [
    {
      title: '項次',
      dataIndex: '序號',
      key: '序號',
      width: 55,
      align: 'center' as const,
    },
    {
      title: '規則名稱',
      dataIndex: '規則名稱',
      key: '規則名稱',
      width: 140,
      render: (text: string, record: RuleRow) => {
        const isEditing = editingRowKey === record.key;

        if (isEditing) {
          return (
            <Input
              value={draftName}
              onChange={e => setDraftName(e.target.value)}
              size='small'
              disabled={savingRow}
              style={{ fontSize: '13px' }}
            />
          );
        }

        return <span style={{ wordBreak: 'break-all' }}>{text}</span>;
      },
    },
    {
      title: '規則內容',
      dataIndex: '規則內容',
      key: '規則內容',
      render: (text: string, record: RuleRow) => {
        const isEditing = editingRowKey === record.key;

        if (isEditing) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Input.TextArea
                value={draftContent}
                onChange={e => setDraftContent(e.target.value)}
                autoSize={{ minRows: 3 }}
                disabled={savingRow}
                style={{ fontSize: '13px' }}
              />
              <Space size={10}>
                <Tooltip title='儲存'>
                  <Button
                    size='small'
                    type='primary'
                    icon={<CheckOutlined />}
                    loading={savingRow}
                    onClick={() => onSaveRow?.(record.key, draftName, draftContent)}
                  />
                </Tooltip>
                <Tooltip title='取消'>
                  <Button
                    size='small'
                    icon={<CloseOutlined />}
                    disabled={savingRow}
                    onClick={() => onCancelEdit?.()}
                  />
                </Tooltip>
                <Popconfirm
                  title='確定要刪除這條規則嗎？'
                  okText='刪除'
                  okType='danger'
                  cancelText='取消'
                  disabled={savingRow}
                  onConfirm={() => onDeleteRow?.(record.key)}
                >
                  <Tooltip title='刪除此規則'>
                    <Button size='small' danger icon={<DeleteOutlined />} disabled={savingRow} />
                  </Tooltip>
                </Popconfirm>
              </Space>
            </div>
          );
        }

        const isExpanded = expandedRows.indexOf(record.key) !== -1;
        const needsTruncation = text.length > 150;

        return (
          <div>
            <div
              style={{
                whiteSpace: isExpanded ? 'pre-wrap' : 'normal',
                overflow: isExpanded ? 'visible' : 'hidden',
                textOverflow: isExpanded ? 'clip' : 'ellipsis',
                display: isExpanded ? 'block' : '-webkit-box',
                WebkitLineClamp: isExpanded ? 'unset' : 3,
                WebkitBoxOrient: 'vertical',
                lineHeight: '1.6',
              }}
            >
              {text}
            </div>
            {needsTruncation && (
              <Button
                type='link'
                size='small'
                style={{ padding: 0, height: 'auto', marginTop: '4px' }}
                onClick={() => {
                  if (isExpanded) {
                    onExpandChange(expandedRows.filter(k => k !== record.key));
                  } else {
                    onExpandChange([...expandedRows, record.key]);
                  }
                }}
              >
                {isExpanded ? '▲ 收起' : '▼ 展開'}
              </Button>
            )}
          </div>
        );
      },
    },
    // 編輯欄：僅在 editMode 時顯示
    ...(editMode
      ? [
          {
            title: '',
            key: 'action',
            width: 40,
            align: 'center' as const,
            render: (_: any, record: RuleRow) => {
              const isEditing = editingRowKey === record.key;
              if (isEditing) return null;
              return (
                <Tooltip title={editingRowKey ? '請先完成目前編輯' : '編輯此規則'}>
                  <Button
                    type='text'
                    size='small'
                    icon={
                      <EditOutlined
                        style={{ fontSize: '14px', color: editingRowKey ? '#ccc' : '#1E88A8' }}
                      />
                    }
                    disabled={!!editingRowKey}
                    style={{ padding: 0 }}
                    onClick={() => onEditRow?.(record.key)}
                  />
                </Tooltip>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {showExpandAll && (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}
        >
          <Button
            type='link'
            size='small'
            onClick={() => {
              if (expandedRows.length === rules.length) {
                onExpandChange([]);
              } else {
                onExpandChange(rules.map(r => r.key));
              }
            }}
            style={{ padding: '0 4px', fontSize: '13px' }}
            disabled={rules.length === 0}
          >
            {expandedRows.length === rules.length ? '▲ 全部收合' : '▼ 全部展開'}
          </Button>
          <span
            style={{
              background: '#1E88A8',
              color: '#fff',
              padding: '3px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            共 {rules.length} 條規則
          </span>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={rules}
        size='small'
        pagination={false}
        scroll={{ y: 'calc(100vh - 340px)' }}
        bordered
        style={{ flex: 1 }}
      />
    </div>
  );
};

export default RulePreviewTable;
